import { useQuery } from "@tanstack/react-query";
import React, { cloneElement, useMemo } from "react";

import type { ILoqKeyStatusDto, UserDetailsDto, UserTicketDetailsDto } from "@/api/types";
import imgRemsLogoSmall from "@/assets/images/integrations/rems-logo-small.svg";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import type { IconComponent } from "@/components/Icon/Icon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { Key02Icon } from "@/components/Icons/Key02Icon";
import { LayersThree01Icon } from "@/components/Icons/LayersThree01Icon";
import { Mail01Icon } from "@/components/Icons/Mail01Icon";
import { PhoneIcon } from "@/components/Icons/PhoneIcon";
import { RefreshCcw01Icon } from "@/components/Icons/RefreshCcw01Icon";
import { ReinviteUserModal } from "@/components/ReinviteUserModal/ReinviteUserModal";
import { User } from "@/components/User/User";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { GridItem } from "@/design-system/Grid";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Tooltip } from "@/design-system/Tooltip";
import { getFlagIcon } from "@/helpers/flags";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useGeneralQueries } from "@/queries/general/queryOptions";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { userMutations } from "@/queries/users/mutations";
import { useUserQueries } from "@/queries/users/queryOptions";
import { useTranslation } from "@/translations";

import { formatDate } from "../FormattedDate/FormattedDate";
import { EyeOffIcon } from "../Icons/EyeOffIcon";
import { Globe02Icon } from "../Icons/Globe02Icon";
import { UserFullNameLink } from "../UserFullNameLink/UserFullNameLink";

type UserDetailsContentProps = {
  user: UserDetailsDto | UserTicketDetailsDto;
  canNavigate?: boolean;
  canReinvite?: boolean;
  showPlatformDetails?: boolean;
  showContactDetails?: boolean;
  showPersonalDetails?: boolean;
  showGroups?: boolean;
  showOtherProjects?: boolean;
  showIntegrations?: boolean;
} & (
  | {
      showAddressDetails: true;
      address: {
        building?: string;
        apartmentType?: string;
        floor?: string;
      };
    }
  | {
      showAddressDetails?: never;
      address?: never;
    }
);

export function UserDetailsContent({
  user,
  address,
  canNavigate = false,
  canReinvite = false,
  showAddressDetails = true,
  showPlatformDetails = false,
  showContactDetails = true,
  showPersonalDetails = false,
  showGroups = false,
  showOtherProjects = false,
  showIntegrations = false,
}: UserDetailsContentProps): React.ReactNode {
  const [isReinviteUserModalOpened, reinviteUserModalHandler] = useBool(false);
  const [isResetIloqKeyModalOpened, resetIloqKeyModalHandler] = useBool(false);
  const sessionUser = useSessionUser();
  const { t, i18n } = useTranslation();
  const hasPermission = usePermission();
  const resetIloqKeyMutation = userMutations.useResetIloqKey();

  const generalQueries = useGeneralQueries();
  const userQueries = useUserQueries();
  const permissionQueries = usePermissionQueries();

  const { data: roles } = useQuery({
    ...permissionQueries.listRoles(),
    enabled: showPlatformDetails,
  });
  const { data: languages } = useQuery(generalQueries.languages());
  // All queries are non-blocking hence why no loading state or error handling is needed
  const { data: signUpReminders } = useQuery({
    ...userQueries.signUpReminders({
      userId: user.id,
    }),
    enabled: canReinvite,
  });
  const { data: userProjectsData } = useQuery({
    ...userQueries.listLinkedProjects({
      userId: user.id,
    }),
    enabled: showOtherProjects,
  });
  const canManageIloq = hasPermission((x) => x.integrations.canManageILoq);
  const canManageRems = hasPermission((x) => x.integrations.canManageRems);
  const integrationMapping = "integrationMapping" in user ? user.integrationMapping : undefined;
  const { data: iloqStatus } = useQuery({
    ...userQueries.iloqStatus({ userId: user.id }),
    enabled: showIntegrations && canManageIloq,
  });
  // The key can only be reset once its status has progressed beyond "notStarted"/"ineligible".
  const canResetIloqKey =
    canManageIloq &&
    (iloqStatus?.status === "completed" || iloqStatus?.status === "returned" || iloqStatus?.status === "notStarted");
  const isDeletedBySelf = user.deletedByUserId === user.id;
  const { data: deletionUser } = useQuery({
    ...userQueries.details({
      userId: user.deletedByUserId as string,
    }),
    enabled: sessionUser.isSuperAdmin && Boolean(user?.deletedAt) && Boolean(user?.deletedByUserId) && !isDeletedBySelf,
  });
  const { data: invitedByUser } = useQuery({
    ...userQueries.details({
      userId: user.invitedByUserId as string,
    }),
    enabled: sessionUser.isSuperAdmin && Boolean(user?.invitedByUserId),
  });

  const tooltipReinvite = useMemo(
    () =>
      signUpReminders?.reminders
        ? signUpReminders.reminders
            .map((reminder) =>
              t("component.user-details-content.tooltip.invite-user", {
                date: formatDate(i18n, "date", reminder.lastReminded),
              }),
            )
            .join("\n")
        : undefined,
    [signUpReminders, t, i18n],
  );
  const userLanguageId = user.autoTranslationLanguageId || user.uiLanguageId;
  const userLanguage = languages?.find((x) => x.id === userLanguageId);

  const otherProjects = useMemo(
    () => userProjectsData?.filter((project) => project.id !== sessionUser.projectId) || [],
    [userProjectsData, sessionUser],
  );
  const showHiddenHouseNumberIndicator = sessionUser.id === user.id && user.hideHouseNumber && !sessionUser.isAdmin;
  const userRole = roles?.find((role) => role.id === user.roleId);
  const invitedAtValue = useMemo(() => {
    if (!("invitedAt" in user) || !user.invitedAt) {
      return null;
    }

    if (invitedByUser) {
      return (
        <Inline gap="sm" align="center">
          {t("component.user-details-content.label.invited-by-date", {
            date: formatDate(i18n, "date", user.invitedAt),
          })}
          <UserFullNameLink user={invitedByUser} />
        </Inline>
      );
    }

    return formatDate(i18n, "date", user.invitedAt);
  }, [user, invitedByUser, i18n, t]);

  return (
    <>
      <section className="@container/user-details-content flex w-full flex-1 flex-col gap-space-md">
        <User
          user={user}
          renderSubtitle={() =>
            showHiddenHouseNumberIndicator ? (
              <Inline gap="sm" align="center">
                {user.locatedAt}
                <Tooltip tooltip={t("component.user-details-content.tooltip.house-number-hidden")}>
                  <EyeOffIcon tone="secondary" />
                </Tooltip>
              </Inline>
            ) : (
              <span data-testid="user-address">{user.locatedAt}</span>
            )
          }
          shouldNavigate={canNavigate}
        />
        {showPlatformDetails && (
          <UserDetailsContentSection
            items={[
              ...(userRole
                ? [
                    {
                      label: t("component.user-details-content.label.role"),
                      value: userRole.name,
                    },
                  ]
                : []),
              ...("lastActivityDate" in user && user.lastActivityDate
                ? [
                    {
                      label: t("component.user-details-content.label.last-activity"),
                      value: formatDate(i18n, "datetime", user.lastActivityDate),
                    },
                  ]
                : []),
              ...("invitedAt" in user && user.invitedAt
                ? [
                    {
                      label: t("component.user-details-content.label.invited-at"),
                      value: invitedAtValue,
                    },
                  ]
                : []),
              {
                label: t("component.user-details-content.label.registered-at"),
                value: user.registeredAt
                  ? formatDate(i18n, "date", user.registeredAt)
                  : t("page.user-detail.not-registered"),
              },
              ...(user.deleteAfter
                ? [
                    {
                      label: t("component.user-details-content.label.will-be-deleted-at"),
                      value: formatDate(i18n, "date", user.deleteAfter),
                      "data-testid": "deletion-date",
                    },
                  ]
                : []),
              ...(user.deletedAt && !isDeletedBySelf
                ? [
                    {
                      label: t("component.user-details-content.label.deleted-at"),
                      value:
                        deletionUser || user.deletionSource === "rems" ? (
                          <Inline gap="sm" align="center">
                            {t("component.user-details-content.label.deleted-by", {
                              date: formatDate(i18n, "date", user.deletedAt),
                            })}
                            {user.deletionSource === "rems" && (
                              <Inline gap="xs" align="center">
                                <span>{t("common.label.rems")}</span>
                                <img src={imgRemsLogoSmall} width={16} height={16} alt="REMS" />
                              </Inline>
                            )}
                            {deletionUser && <UserFullNameLink user={deletionUser} />}
                          </Inline>
                        ) : (
                          formatDate(i18n, "date", user.deletedAt)
                        ),
                    },
                  ]
                : []),
              ...(user.deletedAt && isDeletedBySelf
                ? [
                    {
                      label: t("component.user-details-content.label.deleted-at"),
                      value: t("component.user-details-content.label.deleted-by-self", {
                        date: formatDate(i18n, "date", user.deletedAt),
                      }),
                    },
                  ]
                : []),
            ]}
          />
        )}
        {canReinvite && (
          <Button
            variant="filled"
            size="sm"
            title={
              signUpReminders?.recentlyInvited
                ? `${tooltipReinvite}\n\n${t("component.user-details-content.tooltip.recently-invited")}`
                : tooltipReinvite
            }
            onClick={reinviteUserModalHandler.setTrue}
            disabled={!sessionUser.isSuperAdmin && signUpReminders?.recentlyInvited}
          >
            {t("component.user-details-content.btn.reinvite")}
          </Button>
        )}
        {showContactDetails && (
          <UserDetailsContentSection
            title={t("component.user-details-content.title.contact-details")}
            items={[
              ...(userLanguage
                ? [
                    {
                      label: t("component.user-details-content.label.language"),
                      value: userLanguage.description,
                      icon: getFlagIcon({
                        languageId: user.autoTranslationLanguageId || user.uiLanguageId,
                        width: 16,
                      }) || <Globe02Icon />,
                    },
                  ]
                : []),
              ...(user.email
                ? [
                    {
                      label: t("component.user-details-content.label.email"),
                      value: user.email,
                      icon: <Mail01Icon />,
                    },
                  ]
                : []),
              ...(user.phoneNumber
                ? [
                    {
                      label: t("component.user-details-content.label.phone-number"),
                      value: user.phoneNumber,
                      icon: <PhoneIcon />,
                    },
                  ]
                : []),
            ]}
          />
        )}
        {showAddressDetails && address && (
          <UserDetailsContentSection
            title={t("component.user-details-card.title.address-details")}
            items={[
              ...(address?.building
                ? [
                    {
                      label: t("component.user-details-content.label.building"),
                      value: address.building,
                      icon: <Building01Icon />,
                      "data-testid": "user-building",
                    },
                  ]
                : []),
              ...(address?.floor
                ? [
                    {
                      label: t("component.user-details-content.label.floor"),
                      value: address.floor,
                      icon: <LayersThree01Icon />,
                    },
                  ]
                : []),
              ...(address?.apartmentType
                ? [
                    {
                      label: t("component.user-details-content.label.apartment-type"),
                      value: address.apartmentType,
                      icon: <Key02Icon />,
                    },
                  ]
                : []),
              ...(showIntegrations && canManageIloq && iloqStatus && iloqStatus.status !== "disabled"
                ? [
                    {
                      label: t("component.user-details-content.label.key-status"),
                      value: (
                        <Inline gap="sm" align="center">
                          <Badge variant={iloqStatusToBadgeVariant(iloqStatus.status)}>
                            {iloqStatus.status === "completed" &&
                              t("component.user-details-content.iloq-key-status.completed")}
                            {iloqStatus.status === "notStarted" &&
                              t("component.user-details-content.iloq-key-status.not-started")}
                            {iloqStatus.status === "returned" &&
                              t("component.user-details-content.iloq-key-status.returned")}
                            {iloqStatus.status === "ineligible" &&
                              t("component.user-details-content.iloq-key-status.ineligible")}
                          </Badge>
                          <Tooltip tooltip={t(iloqStatusToTooltipKey(iloqStatus.status))}>
                            <InfoCircleIcon size={16} tone="tertiary" />
                          </Tooltip>
                          {canResetIloqKey && (
                            <ContextMenu
                              items={[
                                {
                                  value: "reset-iloq-key",
                                  labels: { default: t("component.user-details-content.btn.reset-iloq-key") },
                                  icon: <RefreshCcw01Icon />,
                                  "data-testid": "reset-iloq-key",
                                  callbackFn: resetIloqKeyModalHandler.setTrue,
                                },
                              ]}
                            />
                          )}
                        </Inline>
                      ),
                      icon: <Key02Icon />,
                    },
                  ]
                : []),
              ...(showIntegrations && canManageRems && integrationMapping?.owner === "rems"
                ? [
                    {
                      label: t("component.user-details-content.label.rems-status"),
                      value: (
                        <Stack gap="xs" align="start">
                          <Badge variant={integrationMapping.owner === "rems" ? "success" : "default"}>
                            {integrationMapping.owner === "rems"
                              ? t("component.user-details-content.integration-status.linked")
                              : t("component.user-details-content.integration-status.not-linked")}
                          </Badge>
                          {integrationMapping.owner === "rems" &&
                            (integrationMapping.primaryExternalId || integrationMapping.secondaryExternalId) && (
                              <span className="text-caption text-text-secondary">
                                {[
                                  integrationMapping.primaryExternalId &&
                                    t("component.user-details-content.integration-status.contact-id", {
                                      id: integrationMapping.primaryExternalId,
                                    }),
                                  integrationMapping.secondaryExternalId &&
                                    t("component.user-details-content.integration-status.contract-id", {
                                      id: integrationMapping.secondaryExternalId,
                                    }),
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            )}
                        </Stack>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}
        {showPersonalDetails && (
          <UserDetailsContentSection
            title={t("component.user-details-content.title.personal-details")}
            items={[
              ...(user.occupation
                ? [
                    {
                      label: t("component.user-details-content.label.occupation"),
                      value: user.occupation,
                    },
                  ]
                : []),
              ...(user.biography
                ? [
                    {
                      label: t("component.user-details-content.label.biography"),
                      value: user.biography,
                    },
                  ]
                : []),
            ]}
          />
        )}
        {showGroups && "interestGroups" in user && (
          <UserDetailsContentSimpleListSection
            title={t("component.user-details-content.title.interest-groups")}
            items={user.interestGroups.map((group) => ({
              id: group.id,
              name: group.name,
            }))}
          />
        )}
        {showGroups && "helpCategories" in user && (
          <UserDetailsContentSimpleListSection
            title={t("component.user-details-content.title.help-categories")}
            items={user.helpCategories.map((group) => ({
              id: group.id,
              name: group.name,
            }))}
          />
        )}
        {showOtherProjects && (
          <UserDetailsContentSimpleListSection
            title={t("component.user-details-content.title.other-projects")}
            items={otherProjects}
          />
        )}
      </section>
      <ReinviteUserModal
        userId={user.id}
        isOpened={isReinviteUserModalOpened}
        onOpenChange={reinviteUserModalHandler.set}
      />
      <ConfirmModal
        title={t("component.user-details-content.reset-iloq-key-modal.title")}
        description={t("component.user-details-content.reset-iloq-key-modal.description")}
        tone="warning"
        isLoading={resetIloqKeyMutation.isPending}
        isOpened={isResetIloqKeyModalOpened}
        onResolve={() =>
          resetIloqKeyMutation.mutate(user.id, {
            onSuccess: () => {
              resetIloqKeyModalHandler.setFalse();
            },
          })
        }
        resolveBtnProps={{
          text: t("component.user-details-content.btn.reset-iloq-key"),
        }}
        onReject={resetIloqKeyModalHandler.setFalse}
        onOpenChange={resetIloqKeyModalHandler.set}
      />
    </>
  );
}

function iloqStatusToBadgeVariant(status: ILoqKeyStatusDto) {
  if (status === "completed") {
    return "success";
  }

  if (status === "notStarted") {
    return "warning";
  }

  return "default";
}

function iloqStatusToTooltipKey(status: ILoqKeyStatusDto) {
  switch (status) {
    case "completed":
      return "component.user-details-content.tooltip.iloq-key-status.completed" as const;
    case "notStarted":
      return "component.user-details-content.tooltip.iloq-key-status.not-started" as const;
    case "returned":
      return "component.user-details-content.tooltip.iloq-key-status.returned" as const;
    case "ineligible":
      return "component.user-details-content.tooltip.iloq-key-status.ineligible" as const;
    default:
      return "component.user-details-content.tooltip.iloq-key-status" as const;
  }
}

type UserDetailsContentSectionProps = {
  title?: string;
  items: {
    label: string;
    value: string | React.ReactNode;
    icon?: IconComponent;
    "data-testid"?: string;
  }[];
};

function UserDetailsContentSection({ title, items }: UserDetailsContentSectionProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="flex w-full flex-col gap-space-sm border-b-border-default not-last:border-b not-last:pb-space-md">
      <h4 className="text-body-emphasis">{title}</h4>
      <ul className="flex w-full flex-col gap-space-sm">
        {items.map((item) => (
          <li key={item.label} className="grid grid-cols-12 gap-space-xs">
            <GridItem span={{ base: 12, container: { md: 4 } }}>
              <span className="flex w-full items-center gap-space-xs text-caption text-text-tertiary @md/user-details-content:h-space-lg">
                {item.icon && cloneElement(item.icon, { size: 16 })}
                <span>{item.label}</span>
              </span>
            </GridItem>
            <GridItem span={{ base: 12, container: { md: 8 } }}>
              <span className="text-left wrap-break-word" data-testid={item["data-testid"]}>
                {item.value}
              </span>
            </GridItem>
          </li>
        ))}
      </ul>
    </section>
  );
}

type UserDetailsContentSimpleListSectionProps = {
  title: string;
  items: { id: string; name: string }[];
};

function UserDetailsContentSimpleListSection({ title, items }: UserDetailsContentSimpleListSectionProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="flex w-full flex-col gap-space-sm border-b-border-default not-last:border-b not-last:pb-space-md">
      <h4 className="text-body-emphasis">{title}</h4>
      <ul className="flex w-full flex-col gap-space-xs">
        {items.map((item) => (
          <li key={item.id} className="text-left text-caption">
            {item.name}
          </li>
        ))}
      </ul>
    </section>
  );
}
